import "../admin-theme.css";

export const LOG_COLUMNS = [
  { key: "user", label: "کاربر" },
  { key: "endpoint", label: "Endpoint" },
  { key: "model", label: "مدل" },
  { key: "input_units", label: "Input Units" },
  { key: "output_units", label: "Output Units" },
  { key: "cost_usd", label: "Cost USD" },
  { key: "caching_discount", label: "Caching Discount" },
  { key: "net_cost_usd", label: "Pre-discount Cost USD" },
  { key: "customer_caching_discount_usd", label: "Discount USD" },
  { key: "calculated_cost", label: "Final Cost USD" },
  { key: "net_cost_irr", label: "Pre-discount Cost IRR" },
  { key: "customer_caching_discount_irr", label: "Discount IRR" },
  { key: "cost", label: "Final Cost IRR" },
  { key: "unit_type", label: "Unit Type" },
  { key: "prompt_tokens", label: "Prompt Tokens" },
  { key: "completion_tokens", label: "Completion Tokens" },
  { key: "total_tokens", label: "Total Tokens" },
  { key: "cached_tokens", label: "Cached Tokens" },
  { key: "cache_write_tokens", label: "Cache Write" },
  { key: "reasoning_tokens", label: "Reasoning" },
  { key: "image_tokens", label: "Image" },
  { key: "audio_tokens", label: "Audio" },
  { key: "video_tokens", label: "Video" },
  { key: "provider", label: "Provider" },
  { key: "finish_reason", label: "Finish Reason" },
  { key: "status_code", label: "Status Code" },
  { key: "streamed", label: "Streamed" },
  { key: "duration_ms", label: "Duration (ms)" },
  { key: "status", label: "Status" },
  { key: "created_at", label: "Created At" },
  { key: "completed_at", label: "Completed At" },
  { key: "actions", label: "عملیات" },
];

function formatDate(value: unknown): string {
  if (!value) return "—";
  return new Date(value as string).toLocaleString("fa-IR", { dateStyle: "short", timeStyle: "medium" });
}

function formatDecimal(value: unknown): string {
  if (value === undefined || value === null) return "—";
  return String(value);
}

// The pre-discount charge basis is the amount the caching discount is
// subtracted from, before the fixed margin and plan fee are applied:
//  - cacheable token requests: uncached list basis = provider cost + savings
//  - otherwise (raw <= provider): the charge basis is the provider cost
//  - no provider cost (per-unit priced media): fall back to the stored list
//    price (the only available base price; no discount applies in this path)
function preDiscountUsd(log: Record<string, unknown>): number | null {
  const hasProvider = log.cost_usd !== null && log.cost_usd !== undefined;
  if (hasProvider) {
    return (Number(log.cost_usd) || 0) + (Number(log.caching_savings_usd) || 0);
  }
  const list = Number(log.list_price_usd);
  if (log.list_price_usd !== null && log.list_price_usd !== undefined && Number.isFinite(list)) {
    return list;
  }
  return null;
}

// Convert a computed USD figure to IRR using the stored final cost ratio
// (cost / calculated_cost == the USD rate that was actually applied), so the
// value always matches the wallet debit exactly.
function usdToIrr(usd: number, log: Record<string, unknown>): number | null {
  const finalUsd = Number(log.calculated_cost);
  const finalIrr = Number(log.cost);
  if (Number.isFinite(finalUsd) && finalUsd > 0 && Number.isFinite(finalIrr)) {
    return usd * (finalIrr / finalUsd);
  }
  return null;
}

function formatToken(value: unknown): string {
  if (value === undefined || value === null) return "—";
  const num = Number(value);
  if (isNaN(num)) return String(value);
  return num.toLocaleString("en-US");
}

function statusBadge(status: string) {
  switch (status) {
    case "completed": return <span className="admin-badge admin-badge-success">completed</span>;
    case "pending": return <span className="admin-badge admin-badge-info">pending</span>;
    case "forwarded": return <span className="admin-badge admin-badge-info">forwarded</span>;
    case "failed": return <span className="admin-badge admin-badge-error">failed</span>;
    case "recovery_needed": return <span className="admin-badge admin-badge-warning">recovery_needed</span>;
    default: return <span className="admin-badge admin-badge-info">{status}</span>;
  }
}

const RECOVERABLE_STATUSES = new Set(["authorized", "forwarded", "recovery_needed", "failed"]);

function streamedBadge(streamed: unknown) {
  if (streamed === undefined || streamed === null) return <span className="text-xs" style={{ color: "var(--admin-text-tertiary)" }}>—</span>;
  return streamed
    ? <span className="admin-badge admin-badge-info">بله</span>
    : <span className="admin-badge" style={{ background: "var(--admin-bg-hover)", color: "var(--admin-text-secondary)" }}>خیر</span>;
}

function renderCell(log: Record<string, unknown>, key: string, opts: {
  onRecover?: (log: Record<string, unknown>) => void;
  recoveringId?: number | null;
  canRecover?: boolean;
}) {
  switch (key) {
    case "user":
      return (
        <span className="block max-w-[180px]">
          <span className="block text-xs font-medium truncate" style={{ color: "var(--admin-text-primary)" }}>
            {log.user_name as string || "—"}
          </span>
          <span className="block text-[11px] ltr text-left truncate" style={{ color: "var(--admin-text-tertiary)" }}>
            {(log.user_email as string) || (log.user_phone as string) || "—"}
          </span>
        </span>
      );
    case "endpoint":
      return (
        <span className="text-xs ltr text-left block" style={{ color: "var(--admin-text-secondary)" }}>
          {log.endpoint as string}
        </span>
      );
    case "model":
    case "provider":
    case "finish_reason":
    case "unit_type":
      return (
        <span className="text-xs" style={{ color: "var(--admin-text-secondary)" }}>
          {log[key] as string || "—"}
        </span>
      );
    case "cost_usd":
    case "calculated_cost":
    case "cost":
    case "input_units":
    case "output_units":
    case "customer_caching_discount_usd":
    case "customer_caching_discount_irr":
      return (
        <span className="font-mono text-xs" style={{ color: "var(--admin-text-primary)" }}>
          {formatDecimal(log[key])}
        </span>
      );
    case "net_cost_usd": {
      const basis = preDiscountUsd(log);
      return (
        <span className="font-mono text-xs" style={{ color: "var(--admin-text-primary)" }}>
          {formatDecimal(basis)}
        </span>
      );
    }
    case "net_cost_irr": {
      const basisUsd = preDiscountUsd(log);
      const net = basisUsd !== null ? usdToIrr(basisUsd, log) : (Number.isFinite(Number(log.cost)) ? Number(log.cost) : null);
      return (
        <span className="font-mono text-xs" style={{ color: "var(--admin-text-primary)" }}>
          {formatDecimal(net)}
        </span>
      );
    }
    case "caching_discount": {
      const value = log[key];
      if (value === undefined || value === null || value === 0) {
        return <span className="font-mono text-xs" style={{ color: "var(--admin-text-tertiary)" }}>—</span>;
      }
      return (
        <span className="admin-badge admin-badge-success">{value as number}٪</span>
      );
    }
    case "prompt_tokens":
    case "completion_tokens":
    case "total_tokens":
    case "cached_tokens":
    case "cache_write_tokens":
    case "reasoning_tokens":
    case "image_tokens":
    case "audio_tokens":
    case "video_tokens":
    case "duration_ms":
      return (
        <span className="font-mono text-xs" style={{ color: "var(--admin-text-primary)" }}>
          {formatToken(log[key])}
        </span>
      );
    case "status_code":
      return (
        <span className="font-mono text-xs" style={{ color: "var(--admin-text-secondary)" }}>
          {log[key] as number ?? "—"}
        </span>
      );
    case "streamed":
      return streamedBadge(log[key]);
    case "status":
      return statusBadge(log[key] as string);
    case "actions": {
      const status = log.status as string;
      const billing = log.billing_status as string | undefined;
      const stuck = RECOVERABLE_STATUSES.has(status) && !billing && !log.completed_at;
      if (!stuck || !opts.canRecover) return <span className="text-xs" style={{ color: "var(--admin-text-tertiary)" }}>—</span>;
      const busy = opts.recoveringId === log.id;
      return (
        <button
          type="button"
          className="admin-btn admin-btn-secondary admin-btn-sm"
          disabled={busy || opts.onRecover == null}
          onClick={() => opts.onRecover?.(log)}
        >
          {busy ? "در حال بازیابی…" : "بازیابی"}
        </button>
      );
    }
    case "created_at":
    case "completed_at":
      return (
        <span className="text-xs ltr text-left block whitespace-nowrap" style={{ color: "var(--admin-text-tertiary)" }}>
          {formatDate(log[key])}
        </span>
      );
    default:
      return <span className="text-xs">{log[key] as string || "—"}</span>;
  }
}

export default function AdminLogsTable({ logs, onRecover, recoveringId, canRecover }: {
  logs: Record<string, unknown>[];
  onRecover?: (log: Record<string, unknown>) => void;
  recoveringId?: number | null;
  canRecover?: boolean;
}) {
  const opts = { onRecover, recoveringId, canRecover };
  return (
    <div className="overflow-x-auto">
      <table className="admin-table" style={{ minWidth: "2600px" }}>
        <thead>
          <tr>
            {LOG_COLUMNS.map((col) => (
              <th key={col.key}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {logs.map((log) => (
            <tr key={log.platform_request_id as string || log.id as string}>
              {LOG_COLUMNS.map((col) => (
                <td key={col.key}>{renderCell(log, col.key, opts)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
